import axios, { AxiosInstance, AxiosProgressEvent, AxiosRequestConfig, isAxiosError } from 'axios';

import { resolveApiUrl, resolvePublicUrl } from '../utils/core/endpoints';

/**
 * Standalone axios instance for S3 requests.
 * This instance has no baseURL or interceptors to ensure S3 URLs are requested directly.
 */
export const s3AxiosInstance: AxiosInstance = axios.create();

export interface S3Response<T = unknown> {
  code: number;
  data: T;
  bytes: number;
}

export interface RequestOptions {
  onProgress?: (event: AxiosProgressEvent) => void;
  responseType?: 'arraybuffer' | 'text' | 'json';
}

/**
 * Removes any duplicated forward slashes, ignoring "://"
 */
export const trimUrl = (url: string): string => {
  return url.replace(/([^:])(\/\/+)/g, '$1/');
};

/**
 * Request data from an S3 URL as JSON
 */
export const requestS3URL = async (
  url: string,
  options?: RequestOptions,
): Promise<S3Response<string>> => {
  const config: AxiosRequestConfig = {
    method: 'GET',
    url,
    responseType: options?.responseType || 'text',
    onDownloadProgress: options?.onProgress,
  };

  try {
    const response = await s3AxiosInstance.request<string>(config);
    return {
      code: response.status,
      data: response.data,
      bytes: response.headers['content-length']
        ? parseInt(String(response.headers['content-length']), 10)
        : 0,
    };
  } catch (error: unknown) {
    if (isAxiosError(error) && error.response) {
      throw {
        code: error.response.status,
        message: error.response.statusText || 'Request failed',
      };
    }
    throw error;
  }
};

/**
 * Request data from an S3 URL as ArrayBuffer
 */
export const requestS3URLBinary = async (
  url: string,
  options?: RequestOptions,
): Promise<S3Response<ArrayBuffer>> => {
  const config: AxiosRequestConfig = {
    method: 'GET',
    url,
    responseType: 'arraybuffer',
    onDownloadProgress: options?.onProgress,
  };

  try {
    const response = await s3AxiosInstance.request<ArrayBuffer>(config);
    return {
      code: response.status,
      data: response.data,
      bytes: response.headers['content-length']
        ? parseInt(String(response.headers['content-length']), 10)
        : 0,
    };
  } catch (error: unknown) {
    if (isAxiosError(error) && error.response) {
      throw {
        code: error.response.status,
        message: error.response.statusText || 'Request failed',
      };
    }
    throw error;
  }
};

/**
 * Request data from an S3 URL as text
 */
export const requestS3URLText = async (
  url: string,
  options?: RequestOptions,
): Promise<S3Response<string>> => {
  return requestS3URL(url, { ...options, responseType: 'text' });
};

/**
 * Verify S3 response for NoSuchKey errors
 */
export const verifyS3Response = (presignedURLResponse: string): string => {
  if (
    typeof presignedURLResponse === 'string' &&
    presignedURLResponse.includes('<Code>NoSuchKey</Code>')
  ) {
    throw new Error('NoSuchKey');
  }
  return presignedURLResponse;
};

/**
 * Web API client for backend communication
 */
class WebAPIClient {
  private readonly axiosInstance: AxiosInstance;
  private readonly url: string;
  private readonly publicURL: string;

  constructor() {
    this.url = resolveApiUrl();
    this.publicURL = resolvePublicUrl();
    this.axiosInstance = axios.create({
      baseURL: this.url,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  /**
   * @deprecated Only used for backward compatibility support
   */
  getURL(): string {
    return this.url;
  }

  getPublicURL(): string {
    return this.publicURL;
  }

  /**
   * Send POST request
   */
  async post(
    endpoint: string,
    data: unknown,
    options?: { onProgress?: (event: AxiosProgressEvent) => void; token?: string },
  ): Promise<S3Response> {
    const config: AxiosRequestConfig = {
      method: 'POST',
      url: trimUrl(`${this.url}/${endpoint}`),
      data,
      onUploadProgress: options?.onProgress,
      headers: {},
    };

    if (options?.token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${options.token}`,
      };
    }

    try {
      const response = await this.axiosInstance.request(config);
      return {
        code: response.status,
        data: response.data,
        bytes: response.headers['content-length']
          ? parseInt(String(response.headers['content-length']), 10)
          : 0,
      };
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response) {
        throw {
          code: error.response.status,
          message: error.response.statusText || 'Request failed',
        };
      }
      throw error;
    }
  }

  /**
   * Send GET request
   */
  async get(
    endpoint: string,
    params?: Record<string, unknown>,
    options?: { onProgress?: (event: AxiosProgressEvent) => void; token?: string },
  ): Promise<S3Response> {
    const config: AxiosRequestConfig = {
      method: 'GET',
      url: trimUrl(this.buildQueryRequest(endpoint, params)),
      params,
      onDownloadProgress: options?.onProgress,
      headers: {},
    };

    if (options?.token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${options.token}`,
      };
    }

    try {
      const response = await this.axiosInstance.request(config);
      return {
        code: response.status,
        data: response.data,
        bytes: response.headers['content-length']
          ? parseInt(String(response.headers['content-length']), 10)
          : 0,
      };
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response) {
        throw {
          code: error.response.status,
          message: error.response.statusText || 'Request failed',
        };
      }
      throw error;
    }
  }

  /**
   * Build query string for GET requests
   */
  private buildQueryRequest(endpoint: string, data?: Record<string, unknown>): string {
    const endpointUrl = `${this.url}/${endpoint}`;
    if (!data || Object.keys(data).length === 0) {
      return endpointUrl;
    }

    const queryString = Object.keys(data)
      .map((key) => `${key}=${encodeURIComponent(String(data[key]))}`)
      .join('&');

    return `${endpointUrl}?${queryString}`;
  }
}

let webAPI: WebAPIClient;
export function getWebAPI(): WebAPIClient {
  if (!webAPI) {
    webAPI = new WebAPIClient();
  }
  return webAPI;
}
